import { useState, useEffect, useRef } from 'react';
import useCurrentUser from '../../customHook/useCurrentUser';
import AiICon from '../../assets/images/chatLogo.svg';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import socket from '../../features/socket';
import { v4 as uuidv4 } from 'uuid';
import { useUserHolidays } from '../../customHook/useUserHolidays';
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  ConversationHeader,
  TypingIndicator,
  Avatar,
} from '@chatscope/chat-ui-kit-react';
import { Box, Button } from '@chakra-ui/react';
import { IoChatbubbleEllipses, IoClose } from 'react-icons/io5';
import moment from 'moment';
import AISlip from '../AIFeature/AiSlip';
import AiOfferLetter from '../AIFeature/AiOfferLetter';
import ExperienceLetter from '../AIFeature/AiExprerinceLetter';
import AiRelievingLetter from '../AIFeature/AiRelievingLetter';
import AIReport from '../AIFeature/AIReport';

const STORAGE_KEY = 'chatbot_messages';
const SESSION_KEY = 'chatbot_sessionId';

interface ChatBotProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const ChatBot: React.FC<ChatBotProps> = ({ isOpen, setIsOpen }) => {
  const user = useCurrentUser();
  const isEmployee = user?.role?.toLowerCase?.().includes('employee');
  const [messages, setMessages] = useState<any[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((msg: any) => {
          let component: React.ReactNode = null;
          const {
            rows,
            isTable,
            isExcel,
            componentFlag,
            OfferLetterData,
            ExperienceLetterData,
            PayslipData,
            RelievingLetterData,
            isDoc,
          } = msg;

          if (componentFlag === 'relieving_letter' && RelievingLetterData) {
            component = (
              <AiRelievingLetter details={RelievingLetterData} isDoc={isDoc} />
            );
          } else if (componentFlag === 'offer_letter' && OfferLetterData) {
            component = (
              <AiOfferLetter details={OfferLetterData} isDoc={isDoc} />
            );
          } else if (
            componentFlag === 'experience_letter' &&
            ExperienceLetterData
          ) {
            component = (
              <ExperienceLetter
                details={ExperienceLetterData}
                isDoc={isDoc}
              />
            );
          } else if (componentFlag === 'payslip' && PayslipData) {
            component = <AISlip payslipData={PayslipData} isDoc={isDoc} />;
          } else if (rows?.length > 0) {
            component = (
              <AIReport rows={rows} isTable={isTable} isExcel={isExcel} />
            );
          }

          return { ...msg, component };
        });
      }
    } catch (e) {
      console.error('Failed to load chatbot messages from localStorage', e);
    }
    return [];
  });
  const { holidays } = useUserHolidays();
  useEffect(() => {
    if (!user) return;

    setMessages((prev) => {
      if (prev.length > 0) return prev;
      return [
        {
          message: `Hi, I'm TARA, how can i assist you today?`,
          sender: 'AI',
          sentTime: moment().toString(),
          tags: isEmployee
            ? ['Leave Request', 'My Attendance', 'Monthly Hours']
            : ['Generate Letters', 'List All Employees', 'Generate Report'],
          direction: 'incoming',
          component: null,
          isTable: false,
        },
      ];
    });
  }, [user, isEmployee]);

  useEffect(() => {
    let offlineMessageShown = false;

    const handleDisconnect = () => {
      setIsTyping(false);
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
        if (!offlineMessageShown) {
          offlineMessageShown = true;
          setMessages((prev) => [
            ...prev,
            createIncomingMessage({
              message: 'Tara seems to be offline right now. Please try again.',
              tags: ['🔁 Try Again'],
            }),
          ]);
        }
      }
    };

    const handleReconnect = () => {
      offlineMessageShown = false;
    };

    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleDisconnect);
    socket.on('reconnect', handleReconnect);
    socket.on('connect', handleReconnect);

    return () => {
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleDisconnect);
      socket.off('reconnect', handleReconnect);
      socket.off('connect', handleReconnect);
    };
  }, []);

  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(SESSION_KEY);
      if (saved) return saved;
    } catch (e) {
      console.warn('Failed to read session id from localStorage', e);
    }
    return uuidv4();
  });
  const responseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const createIncomingMessage = ({
    message,
    tags = [],
    component = null,
    componentFlag,
    isTable,
    isExcel,
    rows,
  }: {
    message: string;
    tags?: string[];
    component?: any;
    componentFlag?: string;
    rows?: any[];
    isTable?: boolean;
    isExcel?: boolean;
  }) => ({
    direction: 'incoming',
    message,
    tags,
    isTable,
    isExcel,
    rows,
    component,
    componentFlag,
  });

  const handleSend = (text: string) => {
    const prefix = isEmployee ? 'ai:employee' : 'ai:chatbot';
    const eventName = `${prefix}:dashboard`;
    const errorEvent = `${prefix}:error`;
    const resEvent = `${prefix}:response`;

    if (!text.trim()) return;
    setMessages((prev) => [...prev, { direction: 'outgoing', message: text }]);
    setUserInput('');
    setIsTyping(true);

    const payload = {
      message: text,
      companyId: user?.companyId,
      employeeId: user?._id,
      sessionId,
      holidays,
    };

    let hasErrorBeenHandled = false;

    socket.off(resEvent);
    socket.off(errorEvent);

    const responseTimeout = setTimeout(() => {
      responseTimeoutRef.current = null;
      if (!hasErrorBeenHandled) {
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          createIncomingMessage({
            message: 'The request timed out. Please try again shortly.',
            tags: ['🔁 Retry', '🔙 Back'],
          }),
        ]);
      }
    }, 180000);

    responseTimeoutRef.current = responseTimeout;

    socket.emit(eventName, payload);

    socket.once(resEvent, (response) => {
      clearTimeout(responseTimeout);
      responseTimeoutRef.current = null;
      const {
        message,
        tags,
        rows,
        component,
        OfferLetterData,
        ExperienceLetterData,
        PayslipData,
        RelievingLetterData,
        isTable,
        isDoc,
        isExcel,
      } = response;
      let finalMessage = message || 'Response generated successfully.';

      if (component === 'relieving_letter' && RelievingLetterData) {
        setMessages((prev) => [
          ...prev,
          createIncomingMessage({
            message: finalMessage,
            isTable,
            tags: tags || ['View Milestones', 'Back to Project'],
            componentFlag: component,
            component: (
              <AiRelievingLetter
                details={RelievingLetterData}
                isDoc={isDoc}
              />
            ),
          }),
        ]);
        setIsTyping(false);
        return;
      }

      if (component === 'offer_letter' && OfferLetterData) {
        setMessages((prev) => [
          ...prev,
          createIncomingMessage({
            message: finalMessage,
            isTable,
            tags: tags || ['View Milestones', 'Back to Project'],
            componentFlag: component,
            component: (
              <AiOfferLetter details={OfferLetterData} isDoc={isDoc} />
            ),
          }),
        ]);
        setIsTyping(false);
        return;
      }

      if (component === 'experience_letter' && ExperienceLetterData) {
        setMessages((prev) => [
          ...prev,
          createIncomingMessage({
            message: finalMessage,
            isTable,
            tags: tags || ['View Milestones', 'Back to Project'],
            componentFlag: component,
            component: (
              <ExperienceLetter
                details={ExperienceLetterData}
                isDoc={isDoc}
              />
            ),
          }),
        ]);
        setIsTyping(false);
        return;
      }

      if (component === 'payslip' && PayslipData) {
        setMessages((prev) => [
          ...prev,
          createIncomingMessage({
            message: finalMessage,
            isTable,
            tags: tags || ['View Payslip', 'Back to Profile'],
            componentFlag: component,
            component: <AISlip payslipData={PayslipData} isDoc={isDoc} />,
          }),
        ]);
        setIsTyping(false);
        return;
      }

      setMessages((prev) => [
        ...prev,
        createIncomingMessage({
          message: finalMessage,
          isTable,
          isExcel,
          tags: tags || [],
          componentFlag: component,
          component:
            rows?.length > 0 ? (
              <AIReport rows={rows} isTable={isTable} isExcel={isExcel} />
            ) : null,
          rows: rows,
        }),
      ]);

      setIsTyping(false);
    });

    socket.once(errorEvent, (err) => {
      clearTimeout(responseTimeout);
      responseTimeoutRef.current = null;
      if (hasErrorBeenHandled) return;
      hasErrorBeenHandled = true;

      let userFriendlyMessage = 'Something went wrong. Please try again.';
      const rawMsg = err?.message?.toLowerCase?.() || '';
      if (
        rawMsg.includes('rate limit') ||
        rawMsg.includes('free-models-per-day') ||
        rawMsg.includes('429')
      ) {
        userFriendlyMessage =
          'You have reached the daily AI limit. Please try again tomorrow or upgrade the plan.';
      } else if (rawMsg.includes('overload') || rawMsg.includes('503')) {
        userFriendlyMessage =
          'Our AI assistant is currently overloaded. Please wait a few seconds and try again.';
      } else if (rawMsg.includes('timeout')) {
        userFriendlyMessage =
          'The request timed out. Please try again shortly.';
      } else if (rawMsg.includes('network')) {
        userFriendlyMessage =
          "We're having trouble connecting. Please check your internet and try again.";
      } else if (rawMsg.includes('google') || rawMsg.includes('generativeai')) {
        userFriendlyMessage =
          'There’s a temporary issue with our AI model. Please retry in a moment.';
      }

      setMessages((prev) => [
        ...prev,
        createIncomingMessage({
          message: userFriendlyMessage,
          tags: ['🔁 Retry', '🔙 Back'],
        }),
      ]);

      setIsTyping(false);
    });
  };

  const handleSuggestedMessageClick = (message: string) => {
    handleSend(message);
  };

  useEffect(() => {
    if (user?.name) {
      setMessages((prev) => [
        {
          ...prev[0],
          message: `Hi, I'm TARA, how can i assist you today?`,
        },
        ...prev.slice(1),
      ]);
    }
  }, [user]);

  // Persist sessionId to localStorage
  useEffect(() => {
    if (sessionId) {
      try {
        localStorage.setItem(SESSION_KEY, sessionId);
      } catch (e) {
        console.error('Failed to save session to localStorage:', e);
      }
    }
  }, [sessionId]);

  // Persist messages to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      try {
        const toSave = messages.map(({ component, componentFlag, ...rest }) => ({
          ...rest,
          componentFlag,
        }));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      } catch (e) {
        console.error('Failed to save messages to localStorage:', e);
      }
    }
  }, [messages]);

  return (
    <>
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          position="fixed"
          bottom="20px"
          right="20px"
          zIndex={1000}
          borderRadius="full"
          width="60px"
          height="60px"
          color="white"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
          className="ai-chatbot"
        >
          <IoChatbubbleEllipses size={30} color="#6e3ef5" />
        </Button>
      )}

      {isOpen && (
        <Box
          position="fixed"
          bottom="20px"
          right="20px"
          zIndex={1000}
          width="480px"
          height="580px"
          backgroundColor="white"
          borderRadius="8px"
          boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
        >
          <MainContainer style={{ borderRadius: '8px' }}>
            <ChatContainer>
              <ConversationHeader style={{ backgroundColor: '#ECE8FF' }}>
                <Avatar name="AI Assistant" size="md" src={AiICon} />
                <ConversationHeader.Content userName="Tara" />
                <ConversationHeader.Actions>
                  <Button
                    onClick={() => setIsOpen(false)}
                    variant="ghost"
                    size="sm"
                    colorScheme="#6E3EF5"
                  >
                    <IoClose size={20} />
                  </Button>
                </ConversationHeader.Actions>
              </ConversationHeader>

              <MessageList
                typingIndicator={
                  isTyping ? (
                    <TypingIndicator content="Tara is thinking..." />
                  ) : null
                }
              >
                {messages.map((msg, index) => (
                  <Box key={index} mb={6}>
                    <Message
                      model={msg}
                      style={{
                        padding: '8px',
                        borderRadius: '10px',
                        maxWidth: '100%',
                        alignSelf:
                          msg.direction === 'incoming'
                            ? 'flex-start'
                            : 'flex-end',
                      }}
                    >
                      <Avatar
                        name={msg.sender}
                        size="sm"
                        src={
                          msg.direction === 'incoming' ? AiICon : user?.avatar
                        }
                      />
                    </Message>

                    {msg.component && (
                      <Box
                        mt={2}
                        ml={msg.direction === 'incoming' ? 10 : 'auto'}
                      >
                        {msg.component}
                      </Box>
                    )}
                    {msg.tags?.length > 0 && (
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        gap="8px"
                        mt={2}
                        ml={msg.direction === 'incoming' ? 10 : 'auto'}
                        justifyContent={
                          msg.direction === 'incoming'
                            ? 'flex-start'
                            : 'flex-end'
                        }
                      >
                        {msg.tags.map((tag: string, tagIndex: number) => (
                          <Button
                            key={`${index}-${tagIndex}`}
                            size="sm"
                            variant="outline"
                            colorScheme="purple"
                            fontSize={'10px'}
                            padding={2}
                            border="1px solid #CFBEFF"
                            onClick={() => handleSuggestedMessageClick(tag)}
                          >
                            {tag}
                          </Button>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </MessageList>

              <MessageInput
                value={userInput}
                onChange={(text) => setUserInput(text)}
                onSend={() => handleSend(userInput)}
                placeholder={
                  isTyping ? 'Tara is thinking...' : 'Ask HR Assistant...'
                }
                attachButton={false}
                disabled={isTyping}
              />
            </ChatContainer>
          </MainContainer>
        </Box>
      )}
    </>
  );
};

export default ChatBot;
